import React, { useEffect, useState } from 'react';
import { OidcProvider, useOidc } from '@axa-fr/react-oidc';
import { OidcConfiguration } from '@axa-fr/oidc-client';
import { LogoOverlay, useWindowFocus } from '@energy-stacks/shared';
import { Box, Typography } from '@mui/material';
import { ESButton } from '@energy-stacks/core/ui';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useTranslation } from 'react-i18next';

export const ESOidcProvider: React.FC<{
  configuration: OidcConfiguration;
  LoadingComponent?: React.FC;
  limitAuthErrorReloads?: boolean;
  children: React.ReactNode;
}> = ({
  configuration,
  LoadingComponent = LogoOverlay,
  limitAuthErrorReloads = false,
  children,
}) => {
  return (
    <OidcProvider
      configuration={configuration}
      authenticatingComponent={LoadingComponent}
      callbackSuccessComponent={LoadingComponent}
      loadingComponent={LoadingComponent}
      authenticatingErrorComponent={() => (
        <AuthErrorFallback
          LoadingComponent={LoadingComponent}
          limitAuthErrorReloads={limitAuthErrorReloads}
        />
      )}
      sessionLostComponent={() => (
        <AuthErrorFallback
          LoadingComponent={LoadingComponent}
          limitAuthErrorReloads={limitAuthErrorReloads}
        />
      )}
      //@ts-expect-error Might be a bug in the library
      callbackErrorComponent={() => (
        <AuthErrorFallback
          LoadingComponent={LoadingComponent}
          limitAuthErrorReloads={limitAuthErrorReloads}
        />
      )}
      serviceWorkerNotSupportedComponent={LoadingComponent}
    >
      {children}
    </OidcProvider>
  );
};

const AUTH_ERROR_RELOADS_KEY = 'es-auth-error-reloads';
const MAX_AUTH_ERROR_RELOADS = 3;
const AUTH_ERROR_RELOADS_WINDOW = 60000;

const canReloadOnAuthError = () => {
  try {
    const now = Date.now();
    const storedAttempts = sessionStorage.getItem(AUTH_ERROR_RELOADS_KEY);
    const { count, windowStartedAt } = storedAttempts
      ? (JSON.parse(storedAttempts) as { count: number; windowStartedAt: number })
      : { count: 0, windowStartedAt: now };
    const isWithinWindow = now - windowStartedAt < AUTH_ERROR_RELOADS_WINDOW;

    if (isWithinWindow && count >= MAX_AUTH_ERROR_RELOADS) {
      return false;
    }

    sessionStorage.setItem(
      AUTH_ERROR_RELOADS_KEY,
      JSON.stringify({
        count: isWithinWindow ? count + 1 : 1,
        windowStartedAt: isWithinWindow ? windowStartedAt : now,
      })
    );

    return true;
  } catch {
    return false;
  }
};

const AuthErrorFallback = ({
  LoadingComponent,
  limitAuthErrorReloads,
}: {
  LoadingComponent: React.FC;
  limitAuthErrorReloads: boolean;
}) => {
  const isFocused = useWindowFocus();
  const [hasStoppedReloading, setHasStoppedReloading] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    if (!limitAuthErrorReloads || canReloadOnAuthError()) {
      window.location.reload();
      return;
    }

    setHasStoppedReloading(true);
  }, [isFocused, limitAuthErrorReloads]);

  return hasStoppedReloading ? <AuthErrorMessage /> : <LoadingComponent />;
};

export const AuthErrorMessage = () => {
  const [t] = useTranslation('userAccess');
  const { logout } = useOidc();

  return (
    <Box
      height="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      px={4}
    >
      <Typography variant="h4" mb={3}>
        {t('authenticationFailed')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={11}>
        {t('authenticationFailedDescription')}
      </Typography>
      <ESButton
        onClick={() => {
          sessionStorage.removeItem(AUTH_ERROR_RELOADS_KEY);
          logout('/');
        }}
        startIcon={<LogoutRoundedIcon />}
        variant="text"
      >
        {t('switchAccount')}
      </ESButton>
    </Box>
  );
};
